import CacheBase2 from '../../../../services/engines/CacheBase2';
import logService from '../../../../services/wss/logService3';

const _name = 'SearchCache';

/**
 * Stores search results, pagination state, and selected entry.
 * Only SearchEngine should use this cache directly.
 * NOTE: does NOT own the query string — that lives in SearchEngine._lastQuery
 * (last executed search) and SearchQueryEngine (current input value). Keeping
 * a third copy here caused query state to drift if SearchEngine.clear() was
 * ever called without going through SearchQueryEngine.
 */
class SearchCache extends CacheBase2 {
    _total = 0;
    _page = 1;
    _selectedEntry = null;

    initialize() {
        this._name = _name;
    }

    /**
     * Replaces the result list and updates pagination state.
     * @param {object[]} items - Search result entries from BE
     * @param {number} total - Total number of matching entries
     * @param {number} page - Current page number
     */
    setResults = (items, total, page) => {
        try {
            this.setCache(items);
            this._total = total;
            this._page = page;
        } catch (error) {
            logService.logError(`${this._name}.setResults: ${error.message}`);
        }
    };

    /** @returns {number} Total number of matching entries across all pages */
    getTotal = () => this._total;

    /** @returns {number} Current page number */
    getPage = () => this._page;

    /**
     * Sets the currently selected entry.
     * @param {object|null} entry - Entry to select, or null to clear
     */
    setSelectedEntry = (entry) => {
        this._selectedEntry = entry;
    };

    /** @returns {object|null} Currently selected entry, or null */
    getSelectedEntry = () => this._selectedEntry;

    /**
     * Updates a single entry in the result list in-place.
     * Also updates selectedEntry if it matches.
     * Used by onJournalUpdated to keep results in sync without a full reload.
     * @param {object} entry - Updated entry from BE
     * @returns {boolean} True if entry was found and updated
     */
    updateEntry = (entry) => {
        try {
            const idx = this._items.findIndex((e) => e.rk === entry.rk);
            if (idx === -1) return false;

            this._items[idx] = entry;
            if (this._selectedEntry?.rk === entry.rk) {
                this._selectedEntry = entry;
            }

            return true;
        } catch (error) {
            logService.logError(`${this._name}.updateEntry: ${error.message}`);
            return false;
        }
    };

    /**
     * Clears all results and resets pagination state.
     */
    clear = () => {
        this.setCache([]);
        this._total = 0;
        this._page = 1;
        this._selectedEntry = null;
    };
}

const searchCache = new SearchCache();
export default searchCache;
